<template>
  <div
    class="infra-service cursor-pointer"
    @click="goToInfraService($event)"
    :class="[{ 'add-disabled': addServiceError?.length }]"
  >
    <div class="info-block">
      <span class="icon-service">
        <infra-service-logo v-if="dataService.icon && dataService.icon !== 'default'" :logo="dataService.icon" />
        <q-icon v-else name="settings" size="30px" color="white"></q-icon>
      </span>
      <div class="service-name">
        <q-spinner class="service-loader" v-if="addserviceLoad" color="grey-7" :thickness="3" size="54px" />
        <span>
          {{ dataService.displayName || dataService.serviceName }}
          <q-tooltip>{{ dataService.displayName || dataService.serviceName }}</q-tooltip>
        </span>
        <sup push @click.stop>
          <q-icon
            v-if="dataService.description && dataService.description.trim().length"
            name="mdi-information-outline"
            color="grey-7"
            size="14px"
          >
            <q-popup-proxy>
              <q-banner class="service-description-markdown" v-html="dataMarkdown" />
            </q-popup-proxy>
          </q-icon>
        </sup>
        <span v-if="dataService.subtitle" class="service-name__subtitle">{{ dataService.subtitle }}</span>
      </div>
    </div>
    <div class="action-block">
      <span class="service-label q-ml-auto q-mr-md">
        <q-icon v-if="isSwisscom" name="engp:engp-ico-swisscom" size="xs"></q-icon>
        <q-icon v-if="isAlibaba" name="engp:engp-ico-alibaba" size="14px"></q-icon>
        <q-icon v-if="isOnprem" name="engp:engp-ico-onprem" size="xs"></q-icon>
        <q-icon v-if="isAzure" name="engp:engp-ico-azure" size="14px"></q-icon>
      </span>
      <div
        class="btn-group"
        @mouseover="activeClass = true"
        @mouseleave="activeClass = false"
        :class="{ hover: activeClass }"
      >
        <q-btn
          v-if="notCanBeDeleted"
          class="btn--no-hover q-mr-sm"
          size="12px"
          style="cursor: not-allowed"
          icon="lock"
          flat
          padding="0"
          :ripple="false"
        >
          <q-tooltip>Can not be removed from service design</q-tooltip>
        </q-btn>
        <span v-else>
          <q-btn
            v-if="dataService.orderable ? !isInDesign || !activeClass : false"
            @click.stop="addToDesign"
            class="btn--no-hover"
            :icon="isInDesign ? 'check' : 'add'"
            flat
            padding="0"
            size="18px"
            :ripple="false"
            :disable="
              !!addServiceError?.length ||
              uiConfig?.disableEditServices?.includes(dataService?.apmid) ||
              uiConfig?.hideServices?.includes(dataService?.apmid) ||
              !currentService
            "
          >
            <q-tooltip v-if="!currentService">Please choose digital service</q-tooltip>
            <q-tooltip v-else-if="addServiceError?.length">{{ addServiceError }}</q-tooltip>
            <q-tooltip v-else-if="!isInDesign">Add to Service Design</q-tooltip>
          </q-btn>
          <q-btn
            v-if="dataService.orderable ? activeClass && isInDesign : false"
            @click.stop="removeFromServiceDesign"
            class="btn--no-hover"
            size="18px"
            icon="remove"
            flat
            padding="0"
            :ripple="false"
          >
            <q-tooltip>Remove from Service Design</q-tooltip>
          </q-btn>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { marked } from 'marked';
import { SERVICE_TYPE } from '@/constatns';
import { SERVICE_DESIGN_ACTIONS } from '@/store/modules/service-design';
import InfraServiceLogo from '@/shared/components/InfraServiceLogo.vue';
import * as uiConfig from '@/shared/ui-config.json';
import { DIGITAL_SERVICES_GETTERS } from '@/store/modules/digital-services';
import { preCreateDraft } from '@/pages/digitalService/api';
import { supportCntrlClick } from '@/shared/helpers';

export default {
  name: 'InfraService',
  components: {
    InfraServiceLogo,
  },
  SERVICE_TYPE,
  props: {
    service: {
      type: Object,
    },
    dataService: {
      type: Object,
    },
    design: {
      type: Array,
    },
  },
  data() {
    return {
      activeClass: false,
      uiConfig: {},
      addserviceLoad: false,
      addServiceError: undefined,
    };
  },
  mounted() {
    this.uiConfig = uiConfig[process.env.VUE_APP_ENV];
  },
  computed: {
    ...mapGetters('digitalServices', {
      currentService: DIGITAL_SERVICES_GETTERS.CURRENT_SERVICE,
    }),
    dataMarkdown() {
      return this.dataService.description && this.dataService.description ? marked(this.dataService.description) : '';
    },
    isInDesign() {
      const category = this.design.find((category) => category.categoryId === this.service.categoryId);
      return category?.infraServices.some((infra) => infra.apmid === this.dataService.apmid);
    },
    notCanBeDeleted() {
      const category = this.design.find((category) => category.categoryId === this.service.categoryId);
      return category?.infraServices.some((infra) => infra.apmid === this.dataService.apmid && infra.notCanBeDeleted);
    },
    isSwisscom() {
      return this.dataService.cloudType.includes('swisscom');
    },
    isAlibaba() {
      return this.dataService.cloudType.includes('alibaba');
    },
    isAzure() {
      return this.dataService.cloudType.includes('azure');
    },
    isOnprem() {
      return this.dataService.cloudType.includes('onpremises');
    },
  },
  methods: {
    ...mapActions('serviceDesign', {
      setInfraService: SERVICE_DESIGN_ACTIONS.SET_INFRASERVICE,
      removeInfraService: SERVICE_DESIGN_ACTIONS.REMOVE_INFRASERVICE,
    }),
    async addToDesign() {
      this.addserviceLoad = true;
      try {
        const data = {
          infraServiceApmId: this.dataService.apmid,
          digitalServiceApmId: this.currentService.apmId,
        };
        await preCreateDraft(data);
        this.setInfraService({ category: this.service, infraService: this.dataService });
        this.$notify(
          'info',
          'Info',
          `${this.dataService.displayName || this.dataService.serviceName} was added to your service design`,
        );
      } catch (error) {
        this.addServiceError = error.message;
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.addserviceLoad = false;
      }
    },
    removeFromServiceDesign() {
      this.removeInfraService({ category: this.service, infraService: this.dataService });
      this.$notify(
        'info',
        'Info',
        `${this.dataService.displayName || this.dataService.serviceName} was removed from your service design`,
      );
    },
    goToInfraService(event) {
      supportCntrlClick(event, `/services/${this.dataService.id}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.infra-service {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px;
  border-radius: 15px;
  font-size: 14px;
  height: 70px;
  transition: all 0.5s;
  background-color: #e1e5f0;

  &.add-disabled {
    opacity: 0.7;
  }
}

.infra-service {
  width: 100%;
  overflow: hidden;
}
.info-block {
  max-width: 100%;
  width: calc(100% - 140px);
  display: flex;
  align-items: center;

  .service-name {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: calc(100% - 50px);

    span {
      line-height: 1;
      font-weight: bold;
      white-space: nowrap;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &__subtitle {
      font-size: 12px;
      font-weight: 400 !important;
    }
  }
  sup {
    margin-left: 3px;
    cursor: pointer;
    position: absolute;
    right: -17px;
    z-index: 99;
  }
}
.icon-service {
  display: flex;
  min-width: 45px;
  height: 45px;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  border-radius: 50%;
  background-color: #fff;
}
.service-label {
  justify-self: flex-end;
  i {
    margin-left: 6px;
    color: #686868;
  }
}
.action-block {
  width: 140px;
  display: flex;
  align-items: center;
  height: 100%;
  z-index: 5;
  padding-left: 10px;
  transition: all 0.5s;
}
.service-loader {
  position: absolute;
  left: -58px;
  top: -14px;
}
</style>
